import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { matchesUrl, pubUrl, searchUrl } from "@findemdev/findem-helpers";
import { DUMMY_APPS } from "../constants/mockData/catalogueApps";
import type { IApp } from "../types/app";
import type { ICardCommentReponse, ICardCommentSaveProps } from "../types/cardComments";
import type { IMacro } from "../types/macros";
import type { ICreateRoadmapProps, IRoadmapCard } from "../types/roadmapcard";

interface AuthenticateUserPayload {
  email: string;
  password: string;
  csrf: string;
}

interface CsrfTokenResponse {
  csrfToken: string;
}

export interface CompanyAutocompleteResult {
  name: string;
  domain: string;
  ref: string;
  logos?: Array<{ ref: string }>;
  [key: string]: unknown;
}

export interface TitleAutocompleteResult {
  title: string;
  current: number;
}

export interface TitlesAutocompleteResponse {
  titles?: TitleAutocompleteResult[];
}

export interface SkillAutocompleteResult {
  skill: string;
  count: number;
}

interface PublicMacrosResponse {
  public_macros?: Record<string, IMacro>;
  ts?: number;
}

interface PrivateMacrosResponse {
  created?: Record<string, IMacro>;
  deleted?: Array<{ macro_id: string }>;
}

interface ProductCategoriesResponse {
  pcat: string[];
}

interface RoadmapCardsResponse {
  data: {
    taskCardsList: IRoadmapCard[];
  };
}

interface RoadmapCardResponse {
  data: {
    taskCardDetails: IRoadmapCard;
  };
}

interface CardCommentsResponse {
  data: ICardCommentReponse;
}

const getLocalCatalogueApps = (): IApp[] =>
  DUMMY_APPS.map(({ component: _component, ...app }) => app);

const mergeMacros = async (
  joid: string,
  fetchWithBQ: (arg: string | FetchArgs) => ReturnType<ReturnType<typeof fetchBaseQuery>>
) => {
  const publicMacrosResult = await fetchWithBQ({
    url: `${searchUrl}/pub/api/fetch_pub_queries?joid=${joid}&ignore_non_leadership_titles=true`,
  });

  if (publicMacrosResult.error) {
    return { error: publicMacrosResult.error as FetchBaseQueryError };
  }

  const publicMacros = publicMacrosResult.data as PublicMacrosResponse | undefined;
  const mergedResults: Record<string, IMacro> = {
    ...(publicMacros?.public_macros ?? {}),
  };

  const privateMacrosResult = await fetchWithBQ({
    url: `${searchUrl}/pub/api/fetch_pvt_updated_queries?joid=${joid}&from_ts=${publicMacros?.ts ?? 0}&ignore_non_leadership_titles=true`,
  });

  if (privateMacrosResult.error) {
    return { error: privateMacrosResult.error as FetchBaseQueryError };
  }

  const privateMacros = privateMacrosResult.data as PrivateMacrosResponse | undefined;

  privateMacros?.deleted?.forEach(({ macro_id }) => {
    delete mergedResults[macro_id];
  });

  if (privateMacros?.created) {
    Object.assign(mergedResults, privateMacros.created);
  }

  return { data: mergedResults };
};

export const frontdoorApi = createApi({
  reducerPath: "frontdoorApi",
  tagTypes: ["RoadmapCards", "RoadmapCard", "CardComments"],
  baseQuery: fetchBaseQuery({
    credentials: "include",
  }),
  endpoints: (builder) => ({
    getCatalogueApps: builder.query<IApp[], void>({
      queryFn: () => ({ data: getLocalCatalogueApps() }),
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getCsrfToken: builder.query<CsrfTokenResponse, void>({
      query: () => `${matchesUrl}/auth/csrf_token`,
    }),
    authenticateUser: builder.mutation<unknown, AuthenticateUserPayload>({
      query: ({ csrf, email, password }) => ({
        url: `${searchUrl}/auth/login`,
        method: "POST",
        body: {
          api: true,
          _csrf: csrf,
          email,
          password,
        },
      }),
    }),
    logoutUser: builder.mutation<unknown, void>({
      query: () => `${matchesUrl}/auth/logout?api=true`,
    }),
    getIndustriesList: builder.query<string[], void>({
      query: () => `${searchUrl}/pub/api/industrylist`,
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getCompanies: builder.mutation<CompanyAutocompleteResult[], { term: string }>({
      query: ({ term }) => ({
        url: `${searchUrl}/pub/api/company_ac`,
        method: "POST",
        body: {
          prefix: term,
          count: 15,
        },
      }),
    }),
    getTitles: builder.mutation<TitlesAutocompleteResponse, { term: string }>({
      query: ({ term }) => ({
        url: `${searchUrl}/pub/api/query_suggest`,
        method: "POST",
        body: {
          prefix: term,
          types: "T",
          count: 15,
        },
      }),
    }),
    getSkills: builder.mutation<SkillAutocompleteResult[], { term: string }>({
      query: ({ term }) => ({
        url: `${searchUrl}/pub/api/profile-skills`,
        method: "POST",
        body: {
          prefix: term,
          type: "SearchProfileSkills",
          count: 15,
        },
      }),
    }),
    getMacros: builder.query<Record<string, IMacro>, { joid: string }>({
      queryFn: async ({ joid }, _api, _extraOptions, fetchWithBQ) =>
        mergeMacros(joid, fetchWithBQ),
      keepUnusedDataFor: 60 * 60,
    }),
    getCapIqIndustries: builder.query<string[], { joid: string }>({
      query: ({ joid }) => `${searchUrl}/pub/api/ciq_industrylist?joid=${joid}`,
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getProductCategories: builder.query<ProductCategoriesResponse, void>({
      query: () => `${searchUrl}/pub/api/pcats`,
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getRoadmapCards: builder.query<RoadmapCardsResponse, void>({
      query: () => `${pubUrl}/pub/api/task-board/cards`,
      providesTags: ["RoadmapCards"],
      keepUnusedDataFor: 60 * 5,
    }),
    getRoadmapCardById: builder.query<RoadmapCardResponse, string>({
      query: (id) => `${pubUrl}/pub/api/task-board/card/${id}`,
      providesTags: (_result, _error, id) => [{ type: "RoadmapCard", id }],
    }),
    upvoteCard: builder.mutation<unknown, { ref: string }>({
      query: ({ ref }) => ({
        url: `${pubUrl}/pub/api/task-board/upvote`,
        method: "POST",
        body: { ref },
      }),
      invalidatesTags: ["RoadmapCards"],
    }),
    addRoadmapCard: builder.mutation<unknown, ICreateRoadmapProps>({
      query: ({ title, description, shortDescription, status, thumbnailImage, tags }) => ({
        url: `${pubUrl}/pub/api/task-board/card`,
        method: "POST",
        body: {
          title,
          description,
          short_description: shortDescription,
          status,
          thumbnail_image: thumbnailImage,
          tags,
        },
      }),
    }),
    getCommentsByCardRef: builder.query<CardCommentsResponse, string>({
      query: (cardRef) => `${pubUrl}/pub/api/task-board/comments?task_card_ref=${cardRef}`,
      providesTags: (_result, _error, cardRef) => [{ type: "CardComments", id: cardRef }],
      keepUnusedDataFor: 60 * 5,
    }),
    addComment: builder.mutation<unknown, ICardCommentSaveProps>({
      query: ({ comment, cardRef }) => ({
        url: `${pubUrl}/pub/api/task-board/comment`,
        method: "POST",
        body: {
          comment,
          task_card_ref: cardRef,
        },
      }),
    }),
  }),
});

export const {
  useAddCommentMutation,
  useAddRoadmapCardMutation,
  useAuthenticateUserMutation,
  useGetCapIqIndustriesQuery,
  useGetCatalogueAppsQuery,
  useGetCommentsByCardRefQuery,
  useGetCompaniesMutation,
  useGetCsrfTokenQuery,
  useGetIndustriesListQuery,
  useGetMacrosQuery,
  useGetProductCategoriesQuery,
  useGetRoadmapCardByIdQuery,
  useGetRoadmapCardsQuery,
  useGetSkillsMutation,
  useGetTitlesMutation,
  useLogoutUserMutation,
  useUpvoteCardMutation,
} = frontdoorApi;
